import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { KeyValue, MeetingStatus, Person } from './types';
import { basedAxios, useStore } from '../../store/Store';
import EventLabel from '../../components/EventLabel/EventLabel';
import cx from 'classnames';
import { Tab, Transition } from '@headlessui/react';
import Chat from './Chat';
import Avatar from './Avatar';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import CheckboxDefaultIcon from '@material-ui/icons/CheckBoxOutlineBlank';
import { Checkbox, FormControlLabel } from '@material-ui/core';
import SystemLabel from '../../components/systemlabel/SystemLabel';
import { encodeToBase64 } from '../../utils/encode.utils';

interface IBookingDetailsProps {
  order: Person;
  availableTimeSlots: any[];
  myMeetings: any[];
  goBack: () => void;
  myOrder: any;
  activeTab?: string;
  fixedTimeslot?: any;
  settings: any;
}

const placeHolderTexts: {
  [key in string]: {
    [key in string]: string;
  };
} = {
  swe: {
    InviteMessage: 'Skriv ett injudningsmeddelande till din mötespartner...',
  },
  eng: {
    InviteMessage: 'Write an invitation message to your meeting partner...',
  },
};

export default function BookingDetails({ order, availableTimeSlots, myMeetings, goBack, settings, myOrder, fixedTimeslot, ...rest }: IBookingDetailsProps) {
  const [sent, setSent] = useState(false);
  const [noTables, setnoTables] = useState(false);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [timeslot, setTimeslot] = useState<any>(null);
  const [inviteMessage, setInviteMessage] = useState('');
  const [activeTab, setActiveTab] = useState<number | null>(null);
  const [blockedTimeslots, setBlockedTimeslots] = useState<any[]>([]);
  const [currentTargetBookedMeetings, setCurrentTargetBookedMeetings] = useState<any[]>([]);
  const [bookTable, setBookTable] = useState(true);
  const [fullBookedMeetingTables, setFullBookedMeetingTables] = useState([]);

  const { firstName, lastName, companyName, title, orderId, profileImage, bio, email, telephone, linkedIn, questions: orderQuestions } = order;
  const { environment, questions } = useStore();
  const { lang, eventId } = environment;
  const { selection, enableQuestionAnswerSearch, questionAnswers } = settings;

  useEffect(() => {
    async function getTableStatus() {
      const response = await basedAxios.get('./matchMaking/GetTableStatus/' + eventId);
      setFullBookedMeetingTables(response.data);
    }
    getTableStatus();
  }, [eventId]);

  const answeredQuestions = useMemo(() => {
    if (!enableQuestionAnswerSearch) return [];
    const questionOptions = questions.reduce((acc: any, { questionOption, questionId }: any) => {
      if (questionOption?.length === 0) return acc;

      return [
        ...acc,
        ...questionOption.map((qo: any) => {
          const optionName = qo?.questionOptionLanguage?.find(({ languageCode }: { languageCode: string }) => languageCode === lang)?.label;
          return {
            name: optionName,
            questionOptionId: qo.questionOptionId,
            questionId,
          };
        }),
      ];
    }, []);

    return questionOptions.filter(
      ({ questionId, questionOptionId }: { questionId: number; questionOptionId: number }) =>
        selection?.includes(questionId) && order.questions.some((q: any) => q.questionId === questionId && q.questionOptionId === questionOptionId)
    );
  }, [selection, questions, lang, enableQuestionAnswerSearch]);

  const getBookedMeetings = useCallback(async () => {
    try {
      const code = encodeToBase64(`${orderId}`);
      const response = await basedAxios.get('./matchMaking/GetBookedMeetingsForOrder/' + eventId + `?code=${code}`);
      setCurrentTargetBookedMeetings(response.data.meetings);
    } catch (error) {
      console.error(error);
    }
  }, [orderId]);

  const saveMeetingRequest = async () => {
    try {
      setLoading(true);
      setBlockedTimeslots(prev => [...prev, timeslot?.meetingTimeslotId]);

      const timeslotId = timeslot?.meetingTimeslotId;
      const response = await basedAxios.post(`./matchMaking/saveMeetingRequest`, {
        message: inviteMessage,
        booker: myOrder?.orderId,
        target: orderId,
        timeslot: timeslotId,
        bookTable: fullBookedMeetingTables.some(fbmt => fbmt.start === timeslot.start && fbmt.end === timeslot.end) ? false : bookTable,
      });
      if (response.data?.success == false && response.data?.message == 'NoTablesAvailable') {
        setnoTables(true);
        setTimeout(() => {
          setnoTables(false);
        }, 20000);
      } else {
        setSent(true);
        setTimeout(() => {
          setSent(false);
        }, 2000);
      }
      setTimeslot(null);
      setInviteMessage('');
    } catch (error) {
      setError(true);
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getBookedMeetings();
  }, [orderId]);

  const hasBookedMeeting = useMemo(
    () => myMeetings.some(m => m.participants.some(p => p.orderId === orderId) && m.status === MeetingStatus.BOOKED),
    [myMeetings]
  );

  const blocked = useMemo(() => {
    return (
      availableTimeSlots.filter(
        at =>
          !currentTargetBookedMeetings.some(
            m =>
              m.status !== MeetingStatus.CANCELLED &&
              m.status !== MeetingStatus.UNBOOKABLE &&
              m.status !== MeetingStatus.TIME_OUT &&
              ((m.start === at.start && m.end == at.end) || (m.start < at.start && m.end > at.start) || (m.start < at.end && m.end >= at.end))
          )
      ).length < 1
    );
  }, [currentTargetBookedMeetings]);

  const bookableTimeslots = useMemo(() => {
    return availableTimeSlots.map(at => {
      at.status = null;

      //Check if the timeslot is blocked by the current target
      if (
        currentTargetBookedMeetings.some(
          m =>
            m.status !== MeetingStatus.CANCELLED &&
            m.status !== MeetingStatus.TIME_OUT &&
            ((m.start == at.start && m.end == at.end) || (m.start < at.start && m.end > at.start) || (m.start < at.end && m.end >= at.end))
        )
      ) {
        at.status = 'blocked';
        console.log('blocked by current target', currentTargetBookedMeetings);
      }

      //Check if the timeslot is blocked by my meetings
      if (
        myMeetings.some(
          m =>
            m.status !== MeetingStatus.CANCELLED &&
            m.status !== MeetingStatus.TIME_OUT &&
            ((m.start == at.start && m.end == at.end) || (m.start < at.start && m.end > at.start) || (m.start < at.end && m.end >= at.end))
        )
      ) {
        at.status = myMeetings.some(
          m =>
            m.status === MeetingStatus.BOOKED &&
            ((m.start == at.start && m.end == at.end) || (m.start < at.start && m.end > at.start) || (m.start < at.end && m.end >= at.end))
        )
          ? 'booked'
          : 'blocked';
      }

      if (blockedTimeslots.includes(at.meetingTimeslotId)) {
        at.status = 'blocked';
      }

      return at;
    });
  }, [availableTimeSlots, currentTargetBookedMeetings, myMeetings, blockedTimeslots, fixedTimeslot]);

  useEffect(() => {
    if (fixedTimeslot) {
      const targetTimeslot = bookableTimeslots.find(at => at.meetingTimeslotId === fixedTimeslot.meetingTimeslotId);

      if (targetTimeslot.status !== 'blocked') {
        setTimeslot(targetTimeslot);
      } else {
        setTimeslot(null);
      }
    }
  }, [fixedTimeslot, bookableTimeslots]);

  return (
    <div className="h-auto min-h-[90vh] rounded-lg overflow-hidden">

      <div className="relative">
        <div className="flex  w-full items-start h-fit pt-6 pb-4 bg-white">
          <div className="flex items-center justify-center">
            <button className="bg-transparent" onClick={goBack}>
              <i className="ri-arrow-left-line" />
            </button>
            <div className="">
              <div
                className={cx(
                  profileImage ? 'bg-white text-white' : ' text-white text-[32px]',
                  'uppercase text-xl  rounded-full  flex justify-center items-center border overflow-hidden border-white'
                )}
              >
                <Avatar size="xl" person={order} className="bg-teal-700 w-20 h-20 " />
              </div>
            </div>
          </div>

          <div className="px-4 flex flex-col gap-y-1 justify-center items-start ">
            <div className="font-bold flex gap-x-2 items-center ">
              {firstName} {lastName}
              {hasBookedMeeting && (
                <div className="font-normal">
                  <div className="flex items-center h-4 gap-x-1 mt-1 ">
                    {email && settings?.hideEmail !== true && (
                      <a
                        target="_blank"
                        href={`mailto:${email}`}
                        className="no-underline  text-red-500  p-1.5 justify-center items-center bg-red-50  h-fit  rounded-lg flex"
                      >
                        <i className="ri-mail-line ri-sm" />
                      </a>
                    )}
                    {linkedIn && (
                      <a
                        href={linkedIn}
                        target="_blank"
                        className="no-underline text-white font-normal p-1.5 bg-[#0a66c2] items-center justify-center  rounded-lg flex"
                      >
                        <i className="ri-linkedin-fill ri-sm" />
                      </a>
                    )}
                  </div>
                </div>
              )}
            </div>
            <div className="flex text-sm">
              {companyName} {title && ` • ${title}`}{' '}
              {hasBookedMeeting && telephone && (
                <a href={`tel:${telephone}`} target="_blank" className="no-underline ml-1 text-neutral-900">
                  {` • ${telephone}`}
                </a>
              )}
            </div>
            <div className="text-sm">
              {questionAnswers?.length > 0 &&
                orderQuestions
                  ?.filter(q => questionAnswers.includes(q.questionId))
                  ?.map((q: any) => q.questionOptionLabel?.find((o: any) => o.languageCode === lang)?.label)
                  ?.join(' • ')}
            </div>
            <div className="w-fit  bg-white text-neutral-600 text-left flex flex-col gap-3 text-sm   ">
              {bio}
              <div className="flex gap-x-2 gap-y-1 flex-wrap text-white ">
                {answeredQuestions?.map(({ name }: KeyValue) => (
                  <div className="flex items-center justify-center gap-x-1    bg-primary font-medium  w-fit px-2 py-1 rounded-xl text-xs ">
                    <i className="ri-hashtag " />
                    <span className="mb-[1px]">{name}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* <div className="flex items-center gap-x-1 mt-1 ">
              {email && settings?.hideEmail !== true && (
                <a
                  target="_blank"
                  href={`mailto:${email}`}
                  className="no-underline  text-red-500  p-1.5 justify-center items-center bg-red-50   rounded-lg flex"
                >
                  <i className="ri-mail-line ri-sm" />
                </a>
              )}
              {linkedIn && (
                <a href={linkedIn} target="_blank" className="no-underline bg-sky-50 p-1.5 text-[#0a66c2] items-center justify-center  rounded-lg flex">
                  <i className="ri-linkedin-fill ri-sm" />
                </a>
              )}
            </div> */}
          </div>
        </div>
      </div>
      <div className="flex flex-col w-full  justify-center items-center">
        <div className="w-full">
          {hasBookedMeeting && settings?.disableChat !== true ? (
            <Chat myOrder={myOrder} order={order} />
          ) : (
            <>
              {blocked || hasBookedMeeting ? (
                hasBookedMeeting ? (
                  <div className="flex-center pt-8 flex-col gap-y-2 border-t border-t-divider">
                    <i className="ri-calendar-check-line ri-xl bg-green-50 h-10 w-10 flex-center rounded-full text-green-600" />
                    <EventLabel name="AlreadyBooked" groupName="CMS.MatchMaking" />
                  </div>
                ) : (
                  <div className="flex-center pt-8 flex-col gap-y-2 border-t border-t-divider">
                    <i className="ri-calendar-close-line ri-xl bg-red-50 h-10 w-10 flex-center rounded-full text-red-600" />
                    <EventLabel name="NoAvailableTimeslots" groupName="CMS.MatchMaking" />
                  </div>
                )
              ) : (
                <div className="w-full">
                  <div className="mx-4 flex flex-col mt-4 gap-y-2">
                    <span className="text-left">
                      <EventLabel name="SelectATimeslot" groupName="CMS.MatchMaking" />
                    </span>
                    <div className={cx('border  border-divider divide-y divide-divider rounded-md  overflow-y-auto max-h-[30vh]')}>
                      {bookableTimeslots.map(at => (
                        <div
                          key={at.meetingTimeslotId}
                          className={cx(
                            'flex p-2 gap-x-2 items-center justify-between ',
                            at.start === timeslot?.start && at.end === timeslot?.end ? 'bg-neutral-100' : '',
                            at.status ? 'text-neutral-400 hover:bg-white cursor-not-allowed ' : 'hover:bg-neutral-100 cursor-pointer'
                          )}
                          onMouseDown={() => !at.status && setTimeslot(at)}
                        >
                          <div className="flex gap-x-2">
                            <div>
                              <i className="ri-time-line" />
                            </div>
                            <div className="w-fit">
                              {new Date(at.start).toLocaleDateString('sv-SE')} {new Date(at.start).toLocaleTimeString('sv-SE')?.substring(0, 5)} -{' '}
                              {new Date(at.end).toLocaleTimeString('sv-SE')?.substring(0, 5)}
                            </div>
                          </div>
                          <div className="flex items-center gap-x-2">
                            {at.status === 'blocked' && <i className="ri-calendar-close-line text-amber-500" />}
                            {at.status === 'booked' && <i className="ri-calendar-check-line text-green-700" />}
                            {at.start === timeslot?.start && at.end === timeslot?.end && <i className="ri-check-line text-green-700" />}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
              <Transition
                show={sent}
                enter="transition-opacity duration-75"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="transition-opacity duration-150"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <div className="flex gap-x-1 w-full items-center justify-center my-2">
                  <i className="ri-check-line text-green-700" />
                  <EventLabel name="MeetingRequestSent" groupName="CMS.MatchMaking" />
                </div>
              </Transition>
              <Transition
                show={noTables}
                enter="transition-opacity duration-75"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="transition-opacity duration-150"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <div className="flex gap-x-1 items-center my-2">
                  <i className="ri-error-warning-line text-danger" />
                  <EventLabel name="NoTablesAvailableForTimeslot" groupName="CMS.MatchMaking" />
                </div>
              </Transition>
              <Transition
                show={error}
                enter="transition-opacity duration-75"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="transition-opacity duration-150"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <div className="flex gap-x-1 items-center my-2">
                  <i className="ri-error-warning-line text-danger" />
                  <EventLabel name="SomethingWentWrong" groupName="CMS.MatchMaking" />
                </div>
              </Transition>
              {timeslot && !hasBookedMeeting && (
                <div className="w-full my-4 mb-6">
                  <div className="mx-4 flex flex-col gap-y-2">
                    <div className="flex justify-between items-center">
                      <div className="flex gap-x-2 items-center">
                        <i className="ri-time-line" />
                        <span className="font-bold">
                          {new Date(timeslot.start).toLocaleTimeString('sv-SE')?.substring(0, 5)} -{' '}
                          {new Date(timeslot.end).toLocaleTimeString('sv-SE')?.substring(0, 5)}
                        </span>
                      </div>
                      {timeslot?.location && (
                        <div className="flex gap-x-2 items-center">
                          <i className="ri-map-pin-2-line text-neutral-500" />
                          <span className="font-bold">{timeslot?.location}</span>
                        </div>
                      )}
                    </div>
                    <div className="ml-2">
                      {timeslot && timeslot?.maxTables && (
                        <FormControlLabel
                          className="option"
                          disabled={fullBookedMeetingTables.some(fbmt => fbmt.start === timeslot.start && fbmt.end === timeslot.end)}
                          onChange={e => setBookTable(prev => !prev)}
                          checked={!fullBookedMeetingTables.some(fbmt => fbmt.start === timeslot.start && fbmt.end === timeslot.end) && bookTable}
                          control={
                            <Checkbox
                              // classes={{ root: classes.root }}
                              disableRipple
                              style={{
                                opacity: fullBookedMeetingTables.some(fbmt => fbmt.start === timeslot.start && fbmt.end === timeslot.end) ? 0.3 : 1,
                                paddingLeft: 0,
                              }}
                              icon={<CheckboxDefaultIcon />}
                              checkedIcon={<CheckBoxIcon className="text-teal-600" />}
                            />
                          }
                          label={
                            <p className='-ml-2'>
                              <EventLabel name="BookTable" groupName="CMS.MatchMaking" />
                            </p>
                          }
                        />
                      )}
                      {fullBookedMeetingTables.some(fbmt => fbmt.start === timeslot.start && fbmt.end === timeslot.end) && (
                        <EventLabel name="TablesAreFullbooked" groupName="CMS.MatchMaking" />
                      )}
                    </div>

                    <textarea
                      className="border border-divider rounded-md p-2 w-full h-32 resize-none focus:outline-none"
                      value={inviteMessage}
                      onChange={({ target }) => setInviteMessage(target.value)}
                      placeholder={placeHolderTexts[lang]?.InviteMessage}
                    />

                    <div className="flex gap-x-2 justify-center">
                      <button
                        className="w-full rounded-md bg-teal-600 text-white flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                        disabled={!inviteMessage || loading}
                        onClick={saveMeetingRequest}
                      >
                        {loading ? (
                          <svg className="animate-spin ml-1 mr-1 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                        ) : sent ? (
                          <i className="ri-check-line text-white" />
                        ) : null}
                        <EventLabel name="SendRequest" groupName="CMS.MatchMaking" />
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
